// This import adds IE9 and above polyfill. Further language features can be supported if required see:
// https://github.com/facebook/create-react-app/blob/master/packages/react-app-polyfill/README.md
import "react-app-polyfill/ie9";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
// Redux state
import { Provider } from "react-redux";
import { store } from "./State/store";
import { setFetchInitialState } from "State/slices/session-slice";
// Components & styles
import App from "./App";
import LoadingHOC from "./Components/LoadingHOC";
import { ViewportProvider } from "./Components/ViewportProvider";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import theme from "./Styles/material-ui-theme";
import "./Styles/reset.scss"; // This allows all default styling to be reset and actively managed thereafter.
import "./Styles/base.scss";
import ScrollToTop from "./Components/ScrollToTop";
import ErrorFallback from "Pages/ErrorFallback";

// Amplitude
import * as amplitude from "@amplitude/analytics-browser";
import { LogLevel } from "@amplitude/analytics-browser/lib/esm/types";

// Auth
import { SUPERTOKEN_SERVER } from "Services/config";
import SuperTokens, { SuperTokensWrapper } from "supertokens-auth-react";
import EmailPassword from "supertokens-auth-react/recipe/emailpassword";
import Session from "supertokens-auth-react/recipe/session";
import ThirdParty, {
  ThirdpartyComponentsOverrideProvider,
} from "supertokens-auth-react/recipe/thirdparty";
import Passwordless from "supertokens-auth-react/recipe/passwordless";
import MultiFactorAuth from "supertokens-auth-react/recipe/multifactorauth";
import { AuthRecipeComponentsOverrideContextProvider } from "supertokens-auth-react/ui";
import { checkFactorsComplete } from "Services/Utils/Auth/checkFactorsComplete";
import { amplitudeTracking } from "Services/Hooks/useAmplitudeTracking";

const initialiseAmplitude = ({
  testEnvironment,
}: {
  testEnvironment: boolean;
}) => {
  amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, {
    serverZone: "EU",
    autocapture: false,
    logLevel: testEnvironment ? LogLevel.Error : LogLevel.None,
  });
};
if (
  process.env.NODE_ENV === "production" &&
  (/^https:\/\/apps-development-(1[0-5]|[1-9])\.intelligentlilli\.com$/.test(
    window.location.origin
  ) ||
    window.location.origin === "https://app.dev.aws.intelligentlilli.com")
) {
  initialiseAmplitude({ testEnvironment: true });
}

if (
  process.env.NODE_ENV === "production" &&
  window.location.origin === "https://app.intelligentlilli.com"
) {
  initialiseAmplitude({ testEnvironment: false });

  Sentry.init({
    dsn: "https://effd6ce625ae4b31af8cc93f91e11e3a@o506894.ingest.sentry.io/5597206",
    // TO DO: Browser tracing does not seem to have been set up, see docs here:
    // https://docs.sentry.io/platforms/javascript/guides/nextjs/performance/instrumentation/automatic-instrumentation/
    integrations: [Sentry.browserTracingIntegration()],
    environment: process.env.NODE_ENV,
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
  });
}

SuperTokens.init({
  appInfo: {
    apiDomain: SUPERTOKEN_SERVER,
    websiteDomain: window.location.origin,
    apiBasePath: "/",
    websiteBasePath: "/login",
    appName: "Lilli Web App",
  },
  languageTranslations: {
    translations: {
      en: {
        // This is to change the copy of the prebuilt ui
        // https://supertokens.com/docs/references/frontend-sdks/prebuilt-ui/translations
        EMAIL_PASSWORD_SIGN_IN_SUBMIT_BTN: "Log in",
        ERROR_EMAIL_INVALID: "Please enter a valid email address",
      },
    },
  },
  style: `
        [data-supertokens~=container] {
            --palette-primary: 166, 220, 213;
            --palette-buttonText: 12, 13, 13; 
            --palette-primaryBorder:  166, 220, 213; 
            font-family: Inter;
        }

        [data-supertokens~=forgotPasswordLink] {
          color: #4B6360; 
          font-size: 14px;
          text-decoration: underline;
          cursor: pointer;
          font-family: Inter;
        }

        [data-supertokens~=link] {
          color: #4B6360; 
          font-size: 14px;
          text-decoration: underline;
          cursor: pointer;
          font-family: Inter;
        }

        [data-supertokens~=secondaryLinkWithLeftArrow] {
          color: #4B6360; 
          font-size: 14px;
          text-decoration: underline;
          cursor: pointer;
          font-family: Inter;
        }

          [data-supertokens~=button] {
          font-family: Inter;
          }

    
          [data-supertokens~=input] {
            font-family: Inter;
            letter-spacing: normal;
          }

        [data-supertokens~=providerContainer] {
            padding-top: 0px
         }

         [data-supertokens~=superTokensBranding] {
                    display: none;
                }
    `,
  recipeList: [
    Session.init({
      maxRetryAttemptsForSessionRefresh: 3,
      tokenTransferMethod: "header",
      sessionTokenBackendDomain: ".intelligentlilli.com",
    }),
    EmailPassword.init({
      preAPIHook: async (context) => {
        let url = context.url;
        let requestInit = context.requestInit;

        amplitudeTracking("Log in", {
          action: "login_attempt",
          type: "email_password",
        });

        return {
          requestInit,
          url,
        };
      },
      postAPIHook: async (context) => {
        const body = await context?.fetchResponse?.json();

        // body.status will be of type "OK" | "WRONG_CREDENTIALS_ERROR" | "FIELD ERROR" | "GENERAL_ERROR" | "SIGN_IN_NOT_ALLOWED"
        if (body?.status !== "OK") {
          amplitudeTracking("Log in", {
            action: "login_unsuccessful",
            type: "email_password",
            error: body.status,
          });
        }
      },
      onHandleEvent: async (context) => {
        if (context.action === "SUCCESS" && context.createdNewSession) {
          // this is after successful login. If the organisation has MFA enabled, we don't want fetchInitialData to run so we must check factors are complete
          // After successful login (first factor), a new session is created.
          const completed = await checkFactorsComplete();

          if (completed) {
            store.dispatch(setFetchInitialState(true));
            amplitudeTracking("Log in", {
              action: "login_successful",
              type: "email_password",
            });
          }
        }
      },
    }),
    MultiFactorAuth.init({
      firstFactors: ["emailpassword"],
    }),
    Passwordless.init({
      contactMethod: "EMAIL",
      onHandleEvent: async (context) => {
        if (context.action === "SUCCESS" && !context.createdNewSession) {
          // This is after successful OTP (MFA) entry. We want initial data to be fetched after successful MFA code entry.
          // After successful email/password login (the first factor), a session is created. For MFA organisations, they will need to put in the OTP which will update their access token and allow them to see the dashboard
          // hence the conditional of !context.createdNewSession as the session is already created at the first factor
          const completed = await checkFactorsComplete();

          if (completed) {
            store.dispatch(setFetchInitialState(true));
          }
        }
      },
    }),
    ThirdParty.init({
      signInAndUpFeature: {
        providers: [
          {
            id: "entra",
            name: "Microsoft",
            logo: (
              <img src="/images/icons/microsoft.png" alt="Microsoft Logo" />
            ),
          },
        ],
      },
      preAPIHook: async (context) => {
        let url = context.url;
        let requestInit = context.requestInit;

        amplitudeTracking("Log in", {
          action: "login_attempt",
          type: "sso",
        });
        return {
          requestInit,
          url,
        };
      },
      postAPIHook: async (context) => {
        const body = await context?.fetchResponse?.json();

        if (body?.status !== "OK") {
          amplitudeTracking("Log in", {
            action: "login_unsuccessful",
            type: "sso",
          });
        }
      },
      onHandleEvent: async (context) => {
        if (context.action === "SUCCESS" && context.createdNewSession) {
          const completed = await checkFactorsComplete();

          if (completed) {
            store.dispatch(setFetchInitialState(true));

            amplitudeTracking("Log in", {
              action: "login_successful",
              type: "sso",
            });
          }
        }
      },
    }),
    MultiFactorAuth.init({
      firstFactors: ["emailpassword", "thirdparty"],
    }),
  ],
});

// Hack to allow us to host on S3 without having fully-fledged SSR on Lambda or something
// https://via.studio/journal/hosting-a-reactjs-app-with-routing-on-aws-s3

// Set up the browser history with the updated location
// (minus the # sign)
const path = (/#!(\/.*)$/.exec(window.location.hash) || [])[1];
if (path) {
  window.location.hash = "";
  window.history.replaceState({}, "", path);
}

// Creating the root node for react
const container = document.getElementById("root");
if (!container) throw new Error("Root container not found");

const root = createRoot(container);

root.render(
  <Provider store={store}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <SuperTokensWrapper>
          <AuthRecipeComponentsOverrideContextProvider
            components={{
              AuthPageHeader_Override: () => {
                return (
                  <div>
                    <img
                      height="53px"
                      src="/logo/lilli-logo-teal.svg"
                      alt="Lilli"
                    />
                    <h1>Login</h1>
                    <a
                      href="login/reset-password"
                      data-supertokens="link linkButton formLabelLinkBtn forgotPasswordLink"
                    >
                      Logging in for the first time?
                    </a>
                    <div data-supertokens="divider" />
                  </div>
                );
              },
            }}
          >
            <ThirdpartyComponentsOverrideProvider
              components={{
                ThirdPartySignInAndUpProvidersForm_Override: ({
                  DefaultComponent,
                  ...props
                }) => {
                  // optionally override the third party providers list..
                  return (
                    <>
                      <DefaultComponent {...props} />
                      <a
                        href="mailto: support@intelligentlilli.com"
                        target="_blank"
                        rel="noreferrer"
                        data-supertokens="link linkButton formLabelLinkBtn forgotPasswordLink"
                        onClick={() => {
                          amplitudeTracking("Log in", {
                            action: "request_sso",
                          });
                        }}
                      >
                        Request Microsoft login
                      </a>
                    </>
                  );
                },
              }}
            >
              <BrowserRouter>
                <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                  <React.StrictMode>
                    <LoadingHOC>
                      <ViewportProvider>
                        <ScrollToTop />
                        <App />
                      </ViewportProvider>
                    </LoadingHOC>
                  </React.StrictMode>
                </Sentry.ErrorBoundary>
              </BrowserRouter>
            </ThirdpartyComponentsOverrideProvider>
          </AuthRecipeComponentsOverrideContextProvider>
        </SuperTokensWrapper>
      </ThemeProvider>
    </StyledEngineProvider>
  </Provider>
);
